import { isDeveloperApp, installToolInterceptors } from '@/developer-tools/toolHttp'
import { API_LOG_TYPE } from "@/constants/api"
import { useUserStore } from "@/store"
import { router } from "@/router"
import { ROUTES } from "@/constants/routes"
import { getRandomColor } from "@/utils/dev"
import { v4 as uuid } from "uuid"

/**
 *
 * @param {import("axios").AxiosInstance} instance
 */
export function setLogInterceptor(instance) {
  if (isDeveloperApp()) {
    installToolInterceptors(instance)
    return
  }
  instance.interceptors.request.use((config) => {
    config.requestId = uuid().split("-")[0]
    config.color = getRandomColor(100, 150)

    const { tokenType, accessToken } = useUserStore()
    config.headers = config.headers || {}
    if (accessToken) {
      config.headers.Authorization = `${tokenType || "Bearer"} ${accessToken}`
    } else {
      delete config.headers.Authorization
    }

    const { requestId, color, url, data, headers, method } = config

    log({
      type: API_LOG_TYPE.REQ,
      requestId,
      color,
      url,
      data,
      method: method.toUpperCase(),
    })

    return config
  })

  instance.interceptors.response.use(
    (response) => {
      const { requestId, color, method, url } = response.config
      log({
        type: API_LOG_TYPE.RES,
        requestId,
        color,
        url,
        data: response.data,
        method: method.toUpperCase(),
      })
      return response
    },
    (error) => {
      const authorization = error.config?.headers?.Authorization
      if (error.response?.status === 401 && authorization?.startsWith("Bearer ")) {
        // 동시 요청 및 이전 세션 응답도 호출부에서 중복 안내하지 않는다.
        error.sessionUnauthorizedHandled = true
        const userStore = useUserStore()
        if (userStore.accessToken && authorization === `Bearer ${userStore.accessToken}`) {
          const currentRoute = router.currentRoute.value
          userStore.clearAuthState()
          if (currentRoute.name !== ROUTES.LOGIN.INDEX) {
            void router.replace({ name: ROUTES.LOGIN.INDEX, query: { next: currentRoute.fullPath } })
          }
        }
      }
      // 에러 응답 로깅
      const { requestId, color, method, url } = error.config || {}
      log({
        type: API_LOG_TYPE.ERROR,
        requestId: requestId || 'unknown',
        color: color || 'red',
        url: url || 'unknown',
        data: error.response?.data,
        method: method?.toUpperCase() || 'UNKNOWN',
      })

      // 에러 객체에 더 나은 메시지 추가
      if (error.response && error.response.data) {
        // 백엔드에서 보낸 에러 메시지가 있으면 그것을 사용
        if (error.response.data.message) {
          error.message = error.response.data.message
        } else if (error.response.data.errors) {
          error.message = `오류: ${error.response.data.errors}`
        }
      }

      return Promise.reject(error)
    }
  )
}

function log({ type, method, color, requestId, url, data, headers }) {
  const typeColor =
    type === API_LOG_TYPE.REQ
      ? "steelblue"
      : type === API_LOG_TYPE.RES
      ? "seagreen"
      : type === API_LOG_TYPE.WARN
      ? "orange"
      : type === API_LOG_TYPE.ERROR
      ? "red"
      : "black"
  console.log(
    `%c[${type}-${requestId}]%c [${method}] ${url}`,
    `background-color: ${typeColor}; color: white`,
    `color: ${color}`,
    data,
    headers,
  )
}
